/**
 * Protected route wrapper - redirects unauthenticated users to login
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOrg?: boolean;
}

export default function ProtectedRoute({ children, requireOrg = false }: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, isLoading, currentOrgId, currentOrganization } = useAuthStore();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <LoadingSpinner className="h-8 w-8 text-primary-600" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to org creation if org required but none selected
  if (requireOrg && !currentOrgId) {
    return <Navigate to="/onboarding/create-organization" replace />;
  }

  // Show loading while org data is being fetched
  // (currentOrgId is persisted but currentOrganization needs to be fetched)
  if (requireOrg && currentOrgId && !currentOrganization) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <LoadingSpinner className="h-8 w-8 text-primary-600" />
      </div>
    );
  }

  return <>{children}</>;
}

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
